import React from "react";
import { Link } from "react-router-dom";
import { MdPerson, MdHandshake } from "react-icons/md";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";
import LegalPagesBrandHeader from "components/legal/LegalPagesBrandHeader";

/**
 * Maquette flux bulles + flèches, même design que /HowItWorks (tokens, header, footer).
 * Route: /HIWexample — la page /HowItWorks n’est pas modifiée.
 */

function FlowArrow() {
  return (
    <div
      className="flex justify-center py-2 text-brand-300 sm:py-3"
      aria-hidden
    >
      <span className="text-3xl font-normal leading-none tracking-tight text-brand-200 drop-shadow-[0_1px_12px_rgba(96,165,250,0.45)] sm:text-4xl">
        ↓
      </span>
    </div>
  );
}

function StepNumberBadge({ n }) {
  return (
    <span
      className="absolute -top-3 left-1/2 z-10 flex h-14 w-14 -translate-x-1/2 items-center justify-center rounded-2xl border border-white/20 bg-[#0B1437] text-2xl font-black text-white shadow-lg md:text-3xl"
      aria-hidden
    >
      {n}
    </span>
  );
}

const stepGradientCardClass =
  "relative flex w-full flex-col items-center justify-center rounded-3xl border border-white/10 bg-gradient-to-br from-brand-500/30 to-brand-600/10 p-6 shadow-[0_20px_50px_rgba(0,0,0,0.35)]";

function FlowBubble({ children, className = "" }) {
  return (
    <div
      className={`flex min-h-[100px] w-full max-w-sm items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-4 py-4 text-center text-sm font-semibold leading-snug text-white sm:min-h-[110px] sm:px-5 sm:text-base ${className}`}
    >
      {children}
    </div>
  );
}

export default function HIWexample() {
  return (
    <main className="min-h-screen bg-[#0B1437] px-5 py-6 text-white sm:py-8">
      <div className="mx-auto w-full max-w-5xl">
        <LegalPagesBrandHeader />
        <header className="text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            <span className="block">How it works</span>
            <span className="mt-2 block sm:mt-3">
              step-by-step for subscribers
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-gray-300 sm:text-base">
            Everyone starts with Sign up. Then pick how you connect your broker—your personal
            broker, or our broker partner—and follow the matching steps.
          </p>
          <p className="mx-auto mt-3 max-w-xl text-xs text-gray-500">
            <span className="font-semibold text-brand-300">HIWexample</span> — prototype
            layout (bulles + convergence).
          </p>
        </header>

        <section className="relative mt-16 flex justify-center">
          <div
            className={`${stepGradientCardClass} h-44 max-w-sm sm:h-48`}
          >
            <StepNumberBadge n={1} />
            <FlowBubble className="max-w-[240px] border-white/20 bg-white/[0.06]">
              Sign up &amp; verify your account
            </FlowBubble>
          </div>
        </section>

        <section className="mt-20">
          <h2 className="text-center text-2xl font-bold sm:text-3xl">
            Choose your path
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-sm text-gray-400">
            Two onboarding sequences—then the same steps to activate copy trading.
          </p>

          <div className="mt-10 flex flex-col items-center">
            <FlowArrow />

            <div className="grid w-full gap-10 lg:grid-cols-2 lg:gap-16">
              <div className="relative rounded-2xl border border-sky-500/30 bg-sky-500/5 p-6 pt-8 sm:p-8 sm:pt-10">
                <StepNumberBadge n={2} />
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/20 text-sky-200">
                    <MdPerson className="h-7 w-7" aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-sky-300">
                      Path 1
                    </p>
                    <h3 className="text-lg font-bold text-white">
                      Use your personal broker
                    </h3>
                  </div>
                </div>
                <div className="mt-6 flex justify-center">
                  <FlowBubble>Fund your account</FlowBubble>
                </div>
              </div>

              <div className="relative rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 pt-8 sm:p-8 sm:pt-10">
                <StepNumberBadge n={2} />
                <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/20 text-emerald-200">
                    <MdHandshake className="h-7 w-7" aria-hidden />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-300">
                      Path 2
                    </p>
                    <h3 className="text-lg font-bold text-white">
                      Use our broker partner
                    </h3>
                  </div>
                </div>
                <div className="mt-6 flex justify-center">
                  <FlowBubble>Verify &amp; fund your account</FlowBubble>
                </div>
              </div>
            </div>

            <FlowArrow />

            <div className="flex w-full max-w-md flex-col items-center gap-3 sm:gap-4">
              <div className={`${stepGradientCardClass} min-h-[7.5rem] w-full sm:min-h-[8.5rem]`}>
                <StepNumberBadge n={3} />
                <FlowBubble className="max-w-md border-white/20 bg-white/[0.06]">
                  Buy a slot
                </FlowBubble>
              </div>
              <FlowArrow />
              <div className={`${stepGradientCardClass} min-h-[7.5rem] w-full sm:min-h-[8.5rem]`}>
                <StepNumberBadge n={4} />
                <FlowBubble className="max-w-md border-white/20 bg-white/[0.06]">
                  Add your trading account
                </FlowBubble>
              </div>
              <FlowArrow />
              <div className={`${stepGradientCardClass} min-h-[7.5rem] w-full sm:min-h-[8.5rem]`}>
                <StepNumberBadge n={5} />
                <FlowBubble className="max-w-md border-white/20 bg-white/[0.06]">
                  Start earning
                </FlowBubble>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-20 rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center">
          <h2 className="text-lg font-bold sm:text-xl">Ready to start?</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-400">
            Sign up first, then open Pricing and Subscription to match your path.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/SignUp"
              className="inline-flex rounded-xl bg-brand-500 px-6 py-3 text-sm font-bold text-white hover:bg-brand-600"
            >
              Sign up
            </Link>
            <Link
              to="/Pricing"
              className="inline-flex rounded-xl border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              View pricing
            </Link>
            <Link
              to="/HowItWorks"
              className="inline-flex rounded-xl border border-brand-400/40 bg-brand-500/10 px-6 py-3 text-sm font-semibold text-brand-200 hover:bg-brand-500/20"
            >
              Open live How it works
            </Link>
          </div>
        </section>

        <footer className="mt-8 border-t border-white/10 pt-5 text-center text-xs text-gray-400 sm:text-sm">
          <p>AlphaSniper 2026 © All Rights Reserved</p>
          <LegalFooterBannerAndLinks />
        </footer>
      </div>
    </main>
  );
}
